import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Mail, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function VerifyEmail() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [editEmail, setEditEmail] = useState(false);

  if (!user) { nav("/login"); return null; }
  if (user.is_email_verified) { nav("/app"); return null; }

  const verify = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const target = user.pending_email || user.email;
      await api.post("/auth/verify-email", { email: target, code });
      toast.success("Email terverifikasi"); await refresh(); nav("/app");
    } catch (err) { toast.error(err?.response?.data?.detail || "Verifikasi gagal"); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success("Kode dikirim ulang"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal"); }
    finally { setBusy(false); }
  };

  const changeEmail = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.patch("/auth/change-email", { new_email: newEmail }); toast.success("Email diperbarui, cek inbox baru"); await refresh(); setEditEmail(false); setNewEmail(""); }
    catch (err) { toast.error(err?.response?.data?.detail || "Gagal ganti email"); }
    finally { setBusy(false); }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-bizma-cream flex items-center justify-center px-4 py-12" data-testid="verify-page">
      <div className="w-full max-w-md">
        <div className="felt-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <img src="/logo-bizma.png" alt="BIZMA" className="w-11 h-11 rounded-full object-cover shadow-sm" />
            <h1 className="text-2xl font-display font-semibold">Verifikasi Email</h1>
          </div>
          <p className="text-sm text-bizma-subtext">Kami mengirim kode 6 digit ke <span className="font-semibold text-bizma-ink" data-testid="verify-email-display">{user.pending_email || user.email}</span>{user.pending_email && <span className="block text-xs mt-1">(email baru — akan aktif setelah verifikasi)</span>}</p>

          {!editEmail && (
            <form onSubmit={verify} className="mt-6 space-y-3" data-testid="verify-form">
              <label className="text-sm font-medium">Kode Verifikasi</label>
              <input required value={code} onChange={(e)=>setCode(e.target.value.replace(/\D/g,"").slice(0,6))} className="input-bizma tracking-[0.4em] text-center text-xl" placeholder="000000" data-testid="verify-code" />
              <button disabled={busy || code.length !== 6} className="btn-primary w-full h-12" data-testid="verify-submit">{busy?"Memverifikasi...":"Verifikasi"}</button>
              <div className="flex flex-col gap-2 pt-2">
                <button type="button" onClick={resend} disabled={busy} className="text-sm text-bizma-terracotta font-medium" data-testid="verify-resend"><Mail size={14} className="inline"/> Kirim ulang kode</button>
                <button type="button" onClick={()=>setEditEmail(true)} className="text-sm text-bizma-subtext" data-testid="verify-edit-email">Salah email? Ubah email</button>
                <button type="button" onClick={doLogout} className="text-sm text-bizma-subtext" data-testid="verify-logout"><LogOut size={12} className="inline"/> Keluar</button>
              </div>
            </form>
          )}

          {editEmail && (
            <form onSubmit={changeEmail} className="mt-6 space-y-3" data-testid="edit-email-form">
              <label className="text-sm font-medium">Email Baru</label>
              <input type="email" required value={newEmail} onChange={(e)=>setNewEmail(e.target.value)} className="input-bizma" placeholder="nama@email.com" data-testid="new-email-input" />
              <button disabled={busy} className="btn-primary w-full h-12" data-testid="save-email-btn">{busy?"Menyimpan...":"Simpan & Kirim Kode Baru"}</button>
              <button type="button" onClick={()=>setEditEmail(false)} className="w-full text-sm text-bizma-subtext" data-testid="cancel-edit-email">Batal</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
